import React, { useState, useEffect, FC } from 'react';
import { Col, List, Row } from 'antd';
import { Button, Input, Scrollable } from '@cloudcar-app/cloudcar-components';
import { SearchOutlined } from '@ant-design/icons';
import useDebounce from 'utils/useDebounce';
import { getDealerFromIdToken } from 'utils/getDataFromJwt';
import { useLazyQuery } from '@apollo/client';
import operations from 'apollo/operations';
import { LitePurchaseIntent } from 'apollo/types';
import { useHistory } from 'react-router-dom';
import dateFormatter from 'utils/dateFormatter';

interface PurchaseIntentSearchProps {
  insuranceSearch?: boolean;
}

const PurchaseIntentSearch: FC<PurchaseIntentSearchProps> = (
  props: PurchaseIntentSearchProps,
) => {
  const { insuranceSearch } = props;

  const history = useHistory();
  const [searchResults, setSearchResults] = useState(
    [] as LitePurchaseIntent[],
  );
  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchValue = useDebounce(searchString);

  const [searchAllPurchaseIntents, { data, loading }] = useLazyQuery<{
    searchLitePurchaseIntent: LitePurchaseIntent[];
  }>(operations.SEARCH_LITE_PURCHASE_INTENT_TO_CSV, {
    fetchPolicy: 'network-only',
  });

  const searchPurchaseIntents = async () => {
    if (searchString && searchString === debouncedSearchValue) {
      await searchAllPurchaseIntents({
        variables: {
          searchInput: { searchString, insuranceSearch },
        },
      });
    }
  };

  useEffect(() => {
    if (data?.searchLitePurchaseIntent) {
      setSearchResults(data.searchLitePurchaseIntent);
    }
  }, [data]);

  useEffect(() => {
    searchPurchaseIntents();
  }, [debouncedSearchValue]);

  const purchaseIntentResults = () => {
    return (
      <List
        itemLayout="horizontal"
        dataSource={searchResults}
        loading={loading}
        renderItem={(item: LitePurchaseIntent) => (
          <List.Item>
            <List.Item.Meta
              description={
                <Row justify="space-between" align="middle">
                  <Col xs={18} style={{ padding: '12px' }}>
                    <h5>
                      {item.customer?.name} {item.customer?.firstSurname}{' '}
                      {item.customer?.rut} - {item.car?.model} {item.car?.year}{' '}
                      - {dateFormatter(item.createdAt)}
                    </h5>
                  </Col>

                  <Col xs={6}>
                    {insuranceSearch ? (
                      <Button
                        mode="text"
                        onClick={() => {
                          localStorage.setItem(
                            'customerRut',
                            item.customerRut || '',
                          );
                          localStorage.setItem(
                            'purchaseIntentId',
                            item.purchaseIntentId,
                          );
                          localStorage.setItem(
                            'dealer',
                            getDealerFromIdToken() || '',
                          );
                          history.push({
                            pathname: `/insuranceProposal/${item.purchaseIntentId}`,
                            state: {
                              from: history.location,
                            },
                          });

                          localStorage.setItem('detailsInViewMode', 'true');
                        }}
                      >
                        {item.insurance?.insuranceProposal
                          ? 'Ver propuesta'
                          : 'Crear propuesta'}
                      </Button>
                    ) : (
                      <Button
                        mode="text"
                        onClick={() => {
                          localStorage.setItem(
                            'customerRut',
                            item.customerRut || '',
                          );
                          localStorage.setItem(
                            'purchaseIntentId',
                            item.purchaseIntentId,
                          );
                          localStorage.setItem(
                            'dealer',
                            getDealerFromIdToken() || '',
                          );
                          history.push({
                            pathname: '/procedureSelection',
                            state: {
                              from: history.location,
                            },
                          });
                        }}
                      >
                        Ver detalles
                      </Button>
                    )}
                  </Col>
                </Row>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  return (
    <Col className="SearchContainer">
      <Input
        placeholder="Ingrese rut o nombre del cliente..."
        compact
        onChange={(e) => setSearchString(e.target.value)}
        value={searchString}
        suffix={<SearchOutlined />}
        style={{ padding: '12px', borderRadius: '12px' }}
      />
      {(searchResults.length !== 0 || loading) && (
        <Scrollable scrollY style={{ maxHeight: '400px' }}>
          <div className="search-results">{purchaseIntentResults()}</div>
        </Scrollable>
      )}
    </Col>
  );
};

PurchaseIntentSearch.defaultProps = {
  insuranceSearch: false,
};

export default PurchaseIntentSearch;
